import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Download,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatDate, formatDateShort } from "../utils/dateFormat";
import { exportToExcel } from "../utils/excelExport";
import NowName, { staffNow } from "./shared/NowName";
import { engagementDisplayName } from "../utils/engagementName";
import { PAGE_SIZE } from "./SortableTable";
import { exportExcelBtnCls } from './shared/ExportExcelButton';

const EXPORT_FILENAMES = {
  lateAccounts: 'Late_Accounts_Report.xlsx',
  lateArchival: 'Late_Archival_Report.xlsx',
  timesheetDefaulters: 'Timesheet_Defaulters_Report.xlsx',
};

const DataTable = ({ data, category }) => {
  const { hasPermission } = useAuth();
  const canExport = hasPermission('qm:view');
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  // Default page size is the app-wide PAGE_SIZE; the picker below
  // still lets you widen or narrow it per table.
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const parseDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "number") {
      // Handle Google Sheets serial date values
      const baseDate = new Date(Date.UTC(1899, 11, 30));
      return new Date(baseDate.getTime() + value * 24 * 60 * 60 * 1000);
    }
    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  // Get unique columns from data based on category
  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];

    if (category === "lateAccounts") {
      return [
        "Client Group",
        "Client",
        "Partner",
        "Director",
        "Manager",
        "Year End",
        "Listed",
        "Days Late",
        "Status",
      ];
    }

    if (category === "lateAccountsSummary") {
      return ["Partner", "Late Accounts"];
    }

    if (category === "timesheetDefaulters") {
      return [
        "Employee Name",
        "Month",
        "Marks Deducted",
      ];
    }

    if (category === "trainingHours") {
      return [
        "Employee Name",
        "Position Code",
        "Position Level",
        "Training Hours",
      ];
    }

    if (category === "trainingHoursSummary") {
      return [
        "Position Group",
        "Total Training Hours",
        "Average Headcount",
        "Average Training Hours",
      ];
    }

    if (category === "lateArchival") {
      return [
        "Client Group",
        "Client",
        "FYE",
        "Partner",
        "Director",
        "Manager",
        "Sign Off Date",
        "Archival Deadline",
        "Archival Date",
        "Days Late",
      ];
    }

    if (category === "lateFsSigning") {
      return [
        "Client Group",
        "Client",
        "FYE",
        "Partner",
        "Director",
        "Manager",
        "Expected Signing Date",
        "Days Late",
        "Status",
      ];
    }

    if (category === "chargeableHours") {
      // For chargeable hours, show all available columns from the data
      const allColumns = new Set();
      
      // Add key fields first
      allColumns.add("User Name");
      allColumns.add("Department");
      
      // Add all other data fields
      data.forEach((item) => {
        if (item.data) {
          Object.keys(item.data).forEach((key) => {
            if (key !== "User Name" && key !== "Department" && key !== "Position") {
              allColumns.add(key);
            }
          });
        }
      });
      
      return Array.from(allColumns);
    }

    if (category === "headcount") {
      return [
        "Division",
        "Current Headcount", 
        "YTD Joiners",
        "YTD Resignees", 
        "Attrition Rate",
        "Latest Data",
        "Recent Months"
      ];
    }

    // For other categories, use existing logic
    const allColumns = new Set();
    data.forEach((item) => {
      if (item.data) {
        Object.keys(item.data).forEach((key) => allColumns.add(key));
      }
    });

    return Array.from(allColumns).slice(0, 8); // Limit to 8 columns for display
  }, [data, category]);

  // Get value from item based on category and column.
  //
  // This feeds sorting AND the Excel export, so every name here is the CURRENT
  // one (`staffNow` / `*Now`): a renamed person or client holds two different
  // snapshot strings across older and newer rows, and sorting or exporting by
  // the snapshot splits one identity into two. The DISPLAY path is the opposite
  // — getCellNode() below prints the snapshot and hangs "(now: …)" off it
  // (docs/data-model.md §2).
  // The engagement as sorting and the export name it: current composed name
  // plus the description headline of a multi-engagement service
  // (utils/engagementName engagementDisplayName).
  const clientLabel = (item) => {
    const name = item.clientNameNow || item.clientName;
    return name ? engagementDisplayName(name, item.serviceDescription) : "";
  };
  const getValue = (item, column) => {
    if (category === "lateAccounts") {
      switch (column) {
        case "Client":
          return (
            clientLabel(item) ||
            (item.clientGroupNow || item.clientGroup) ||
            item.data?.["Client Group"] ||
            item.data?.["Engagement Name"] ||
            ""
          );
        case "Partner":
          return staffNow(item.partner, item.partnerNow) || item.data?.Partner || "";
        case "Director":
          return staffNow(item.director, item.directorNow) || item.data?.Director || "";
        case "Manager":
          return staffNow(item.manager, item.managerNow) || item.data?.["Manager/Assistant Manager 1"] || "";
        case "Year End":
          return (
            parseDateValue(item.finYearEnd || item.data?.["Year End"]) ||
            item.data?.["Year End"] ||
            ""
          );
        case "Status":
          return item.status || item.data?.Status || "";
        default:
          return item.data?.[column] || "";
      }
    }

    if (category === "lateAccountsSummary") {
      switch (column) {
        case "Partner":
          return staffNow(item.partner, item.partnerNow) || item.data?.Partner || "";
        case "Late Accounts":
          return item.lateAccountsCount ?? item.data?.["Late Accounts"] ?? "";
        default:
          return item.data?.[column] || "";
      }
    }

    if (category === "timesheetDefaulters") {
      switch (column) {
        case "Employee Name":
          return item.employeeName || item.data?.["Employee Name"] || "";
        case "Date":
          return (
            parseDateValue(item.recordDate) ||
            parseDateValue(item.data?.["Date"]) ||
            null
          );
        case "Month":
          return item.month || item.data?.["Month"] || "";
        case "Availability (hrs)": {
          const value =
            item.availabilityHours ??
            parseFloat(item.data?.["Availability"] ?? "");
          return isNaN(value) ? null : value;
        }
        case "Working Hours (hrs)": {
          const value =
            item.workingHours ?? parseFloat(item.data?.["Working Hours"] ?? "");
          return isNaN(value) ? null : value;
        }
        case "Chargeability": {
          const rawValue =
            item.chargeability ?? item.data?.["Chargeability"] ?? null;
          if (rawValue === null || rawValue === "") return null;
          const numeric = parseFloat(rawValue);
          return isNaN(numeric) ? rawValue : numeric;
        }
        case "Marks Deducted": {
          const value =
            item.marksDeducted ?? parseFloat(item.data?.["Marks Deducted"] ?? "0");
          return isNaN(value) ? 0 : value;
        }
        case "Status":
          return item.status || item.data?.["Status"] || "";
        case "Remarks":
          return item.remarks ?? item.data?.["Remarks"] ?? "";
        default:
          return "";
      }
    }

    if (category === "trainingHours") {
      switch (column) {
        case "Employee Name":
          return item.employeeName;
        case "Position Code":
          return item.position;
        case "Position Level":
          return item.mappedPosition;
        case "Training Hours":
          return item.trainingHours;
        default:
          return "";
      }
    }

    if (category === "trainingHoursSummary") {
      switch (column) {
        case "Position Group":
          return item.positionGroup;
        case "Total Training Hours":
          return item.totalTrainingHours;
        case "Average Headcount":
          return item.averageHeadcount;
        case "Average Training Hours":
          return item.averageTrainingHours;
        default:
          return "";
      }
    }

    if (category === "lateArchival") {
      switch (column) {
        case "Client Group":
          return (item.clientGroupNow || item.clientGroup) || "";
        case "Client":
          return clientLabel(item);
        case "FYE":
          return item.fye || "";
        case "Partner":
          return staffNow(item.partner, item.partnerNow) || "";
        case "Director":
          return staffNow(item.director, item.directorNow) || "";
        case "Manager":
          return staffNow(item.manager, item.managerNow) || "";
        case "Sign Off Date":
          return item.signOffDate || "";
        // API sends null for empty values — keep showing the legacy sentinel
        // strings (display, search, sort and export all stay unchanged).
        case "Archival Deadline":
          return item.archivalDeadline || "N/A";
        case "Archival Date":
          return item.archivalDate || "Not Archived";
        case "Days Late":
          return item.daysLate ?? "";
        default:
          return item.data?.[column] || "";
      }
    }

    if (category === "lateFsSigning") {
      switch (column) {
        case "Client Group":
          return (item.clientGroupNow || item.clientGroup) || "";
        case "Client":
          return clientLabel(item);
        case "FYE":
          return item.fye || "";
        case "Partner":
          return staffNow(item.partner, item.partnerNow) || "";
        case "Director":
          return staffNow(item.director, item.directorNow) || "";
        case "Manager":
          return staffNow(item.manager, item.managerNow) || "";
        case "Expected Signing Date":
          return item.expectedDate || "";
        case "Days Late":
          return item.daysLate ?? "";
        case "Status":
          return item.status || "";
        default:
          return item.data?.[column] || "";
      }
    }

    if (category === "chargeableHours") {
      if (column === "User Name") {
        return item.userName || item.employeeName;
      } else if (column === "Department") {
        return item.data?.Department || item.position;
      } else if (column === "Position") {
        return item.position;
      } else {
        // For all other columns, get from data or taskHours
        return item.data?.[column] || item.taskHours?.[column] || "";
      }
    }

    if (category === "headcount") {
      switch (column) {
        case "Division":
          return item.division;
        case "Current Headcount":
          return item.currentHeadcount;
        case "YTD Joiners":
          return item.joiners;
        case "YTD Resignees":
          return item.resignees;
        case "Attrition Rate":
          return item.attritionRate;
        case "Latest Data":
          return item.latestMonth;
        case "Recent Months":
          return item.monthlyBreakdown;
        default:
          return "";
      }
    }

    // For other categories, use existing data structure
    return item.data?.[column];
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let filtered = data.filter((item) => {
      if (!searchTerm) return true;

      if (category === "lateAccounts") {
        const searchString = [
          item.clientName,
          item.clientNameNow,
          item.clientGroup,
          item.clientGroupNow,
          item.partner,
          item.partnerNow,
          item.director,
          item.directorNow,
          item.manager,
          item.managerNow,
          item.status,
          item.data?.["Engagement Name"],
          item.data?.["Year End"],
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      if (category === "lateAccountsSummary") {
        const searchString = [
          item.partner,
          item.lateAccountsCount,
        ]
          .filter((value) => value !== undefined && value !== null)
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      if (category === "timesheetDefaulters") {
        const searchString = [
          item.employeeName,
          item.month,
          item.data?.["Date"],
          item.availabilityHours,
          item.workingHours,
          item.chargeability,
          item.remarks,
          item.marksDeducted,
          item.status,
        ]
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      if (category === "trainingHours") {
        const searchString = [
          item.employeeName,
          item.position,
          item.mappedPosition,
          item.trainingHours,
        ]
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      if (category === "trainingHoursSummary") {
        const searchString = [
          item.positionGroup,
          item.totalTrainingHours,
          item.averageHeadcount,
          item.averageTrainingHours,
        ]
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      if (category === "lateArchival") {
        const searchString = [
          item.clientGroup,
          item.clientName,
          item.fye,
          item.partner,
          item.director,
          item.manager,
          item.signOffDate,
          item.archivalDeadline || "N/A",
          item.archivalDate || "Not Archived",
          item.daysLate,
        ]
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      if (category === "lateFsSigning") {
        const searchString = [
          item.clientGroup,
          item.clientName,
          item.fye,
          item.partner,
          item.director,
          item.manager,
          item.expectedDate,
          item.daysLate,
          item.status,
        ]
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      if (category === "chargeableHours") {
        const searchString = [
          item.userName,
          item.employeeName,
          item.position,
          item.totalHours,
          ...Object.values(item.data || {}),
        ]
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      if (category === "headcount") {
        const searchString = [
          item.division,
          item.currentHeadcount,
          item.joiners,
          item.resignees,
          item.attritionRate,
          item.latestMonth,
          item.monthlyBreakdown,
        ]
          .join(" ")
          .toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }

      // For other categories, use existing logic
      const searchString = Object.values(item.data || {})
        .join(" ")
        .toLowerCase();
      return searchString.includes(searchTerm.toLowerCase());
    });

    if (sortField) {
      const convertToDate = (value) => {
        if (!value) return null;
        if (value instanceof Date && !isNaN(value.getTime())) {
          return value;
        }
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (!trimmed) return null;
          if (!(/[\d]/.test(trimmed))) return null;
          if (!(/[\/-]|T/.test(trimmed))) return null;
          const parsed = parseDateValue(trimmed);
          if (parsed instanceof Date && !isNaN(parsed.getTime())) {
            return parsed;
          }
        }
        return null;
      };

      filtered.sort((a, b) => {
        const aValue = getValue(a, sortField);
        const bValue = getValue(b, sortField);

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        const aDate = convertToDate(aValue);
        const bDate = convertToDate(bValue);

        if (aDate || bDate) {
          if (!aDate) return 1;
          if (!bDate) return -1;
          const dateComparison = aDate.getTime() - bDate.getTime();
          return sortDirection === "desc" ? -dateComparison : dateComparison;
        }

        // Smart comparison: detect if both values are numeric
        const aNum = parseFloat(aValue);
        const bNum = parseFloat(bValue);
        
        const aIsNumeric = !isNaN(aNum) && isFinite(aNum) && 
                          (typeof aValue === 'number' || 
                           (typeof aValue === 'string' && aValue.trim() !== '' && !isNaN(parseFloat(aValue.trim()))));
        const bIsNumeric = !isNaN(bNum) && isFinite(bNum) && 
                          (typeof bValue === 'number' || 
                           (typeof bValue === 'string' && bValue.trim() !== '' && !isNaN(parseFloat(bValue.trim()))));

        let comparison = 0;
        
        if (aIsNumeric && bIsNumeric) {
          // Both are numeric - compare as numbers
          comparison = aNum - bNum;
        } else if (aIsNumeric && !bIsNumeric) {
          // Only A is numeric - numbers come first
          comparison = -1;
        } else if (!aIsNumeric && bIsNumeric) {
          // Only B is numeric - numbers come first
          comparison = 1;
        } else {
          // Both are strings - compare as strings (case insensitive)
          const aStr = String(aValue).toLowerCase();
          const bStr = String(bValue).toLowerCase();
          if (aStr > bStr) comparison = 1;
          if (aStr < bStr) comparison = -1;
        }

        return sortDirection === "desc" ? -comparison : comparison;
      });
    }

    return filtered;
  }, [data, searchTerm, sortField, sortDirection, category]);

  // A new search term or category starts from page 1 — otherwise a query that
  // narrows the rows to one page leaves the table stranded on an empty page
  // with no pager to get back.
  useEffect(() => { setCurrentPage(1); }, [searchTerm, category]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedData.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // The display value of one cell: a name column prints its snapshot with the
  // rename after it, everything else falls through to getValue().
  const NAME_CELLS = {
    Partner: ["partner", "partnerNow"],
    Director: ["director", "directorNow"],
    Manager: ["manager", "managerNow"],
    Client: ["clientName", "clientNameNow"],
    "Client Group": ["clientGroup", "clientGroupNow"],
  };
  const getCellNode = (item, column) => {
    const pair = NAME_CELLS[column];
    if (pair && (item[pair[0]] || item[pair[1]])) {
      // Same-company engagements of one service differ only by the description.
      if (column === "Client" && item.serviceDescription) {
        return (
          <span>
            <NowName value={item[pair[0]]} now={item[pair[1]]} fallback="" />
            <span className="block text-xs text-slate-400">{item.serviceDescription}</span>
          </span>
        );
      }
      return <NowName value={item[pair[0]]} now={item[pair[1]]} fallback="" />;
    }
    return getValue(item, column);
  };

  const formatValue = (value, column, category) => {
    if (value === null || value === undefined) return "";
    if (React.isValidElement(value)) return value;
    if (value instanceof Date) {
      return formatDateShort(value);
    }
    // ISO dates arrive as 'YYYY-MM-DD'; datetimes are parsed so the local
    // calendar day is shown.
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return formatDateShort(value);
    }
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      const dateObj = new Date(value);
      if (!isNaN(dateObj.getTime())) return formatDateShort(dateObj);
    }

    if (category === "timesheetDefaulters") {
      if (column === "Chargeability") {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          if (numValue <= 1) {
            return `${(numValue * 100).toFixed(1)}%`;
          }
          if (numValue <= 100) {
            return `${numValue.toFixed(1)}%`;
          }
        }
      }

      if (column === "Availability (hrs)" || column === "Working Hours (hrs)") {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          return numValue.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          });
        }
      }

      if (column === "Marks Deducted") {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          return numValue.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
        }
      }
    }

    // Special formatting for headcount category
    if (category === "headcount") {
      if (column === "Attrition Rate") {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          return `${numValue.toFixed(1)}%`;
        }
      }
      if (column === "Current Headcount" || column === "YTD Joiners" || column === "YTD Resignees") {
        const numValue = parseInt(value);
        if (!isNaN(numValue)) {
          return numValue.toString();
        }
      }
    }

    if (typeof value === "number") {
      return value.toLocaleString();
    }
    return String(value);
  };

  const getCellClassName = (column, value, category) => {
    let baseClass = "px-6 py-4 whitespace-nowrap text-sm";
    
    if ((category === "lateArchival" || category === "lateFsSigning") && column === "Days Late" && typeof value === "number") {
      if (value > 90) {
        baseClass += " text-red-900 font-bold bg-red-50";
      } else if (value > 60) {
        baseClass += " text-red-700 font-semibold bg-red-25";
      } else if (value > 30) {
        baseClass += " text-orange-700 font-medium";
      } else {
        baseClass += " text-gray-900";
      }
    } else if (category === "timesheetDefaulters" && column === "Marks Deducted" && typeof value === "number") {
      if (value >= 3) {
        baseClass += " text-red-700 font-semibold";
      } else if (value > 0) {
        baseClass += " text-orange-600";
      } else {
        baseClass += " text-gray-900";
      }
    } else if (category === "timesheetDefaulters" && column === "Chargeability" && typeof value === "number") {
      const ratio = value <= 1 ? value : value / 100;
      if (!isNaN(ratio) && ratio < 0.7) {
        baseClass += " text-red-700 font-semibold";
      } else if (!isNaN(ratio) && ratio < 0.85) {
        baseClass += " text-orange-600";
      } else {
        baseClass += " text-gray-900";
      }
    } else {
      baseClass += " text-gray-900";
    }
    
    return baseClass;
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No data available for this category.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="relative">
          <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={filteredAndSortedData.length}>
                All ({filteredAndSortedData.length})
              </option>
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          {EXPORT_FILENAMES[category] && canExport && (
            <button
              onClick={() => {
                const rows = filteredAndSortedData.map(item => {
                  const row = {};
                  columns.forEach(col => {
                    const val = getValue(item, col);
                    row[col] = val instanceof Date ? formatDate(val) : (val ?? '');
                  });
                  return row;
                });
                exportToExcel(rows, EXPORT_FILENAMES[category]);
              }}
              className={exportExcelBtnCls}
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="table-scroll">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column}
                    onClick={() => handleSort(column)}
                    className="px-6 py-3 font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 select-none"
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column}</span>
                      {getSortIcon(column)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                    No matching records found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={rowIndex % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50 hover:bg-slate-100"}
                  >
                    {columns.map((column) => {
                      const value = getCellNode(item, column);
                      return (
                        <td key={column} className={getCellClassName(column, value, category)}>
                          {formatValue(value, column, category)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of{" "}
            {filteredAndSortedData.length} results
          </div>

          <div className="flex items-center justify-center space-x-1">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Desktop pagination - show more pages */}
            <div className="hidden sm:flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, currentPage - 2);
                const page = startPage + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === page
                        ? "bg-primary-600 text-white"
                        : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            {/* Mobile pagination - show fewer pages */}
            <div className="flex sm:hidden space-x-1">
              {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                const startPage = Math.max(1, currentPage - 1);
                const page = startPage + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-2 py-2 text-sm font-medium rounded-md min-w-[2.5rem] ${
                      currentPage === page
                        ? "bg-primary-600 text-white"
                        : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
